import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Fab,
  Drawer,
  AppBar,
  Toolbar,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  Psychology as BrainIcon,
  Stop as StopIcon,
  ContentCopy as CopyIcon,
  DeleteOutline as ClearIcon,
  CameraAlt as CameraIcon,
  Image as ImageIcon,
  TravelExplore as TravelExploreIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { iaService, ChatRequest, WebSearchRequest } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import MarkdownContent from './MarkdownContent';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  imagePreview?: string; // data URL for display
}

interface SearchPending {
  query: string;
  motivo: string;
  messageId: number;
  partialContent: string;
}

interface ChatIAProps {
  contexto?: 'dashboard' | 'saude';
  dashboardId?: number;
  submenuId?: number;
  eixo?: string;
  quadrimestre?: string;
  dadosContexto?: string;
  /** Ref to a DOM element to capture as screenshot */
  captureRef?: React.RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Copy Button
// ---------------------------------------------------------------------------

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
      <IconButton size="small" onClick={handleCopy} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
        <CopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Typing Indicator
// ---------------------------------------------------------------------------

const TypingIndicator: React.FC = () => (
  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', py: 0.5 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.400',
          animation: 'typing-bounce 1.4s infinite',
          animationDelay: `${i * 0.2}s`,
          '@keyframes typing-bounce': {
            '0%, 60%, 100%': { transform: 'translateY(0)' },
            '30%': { transform: 'translateY(-4px)' },
          },
        }}
      />
    ))}
  </Box>
);

// ---------------------------------------------------------------------------
// Image Preview (attached to message or pending)
// ---------------------------------------------------------------------------

const ImagePreview: React.FC<{ src: string; onRemove?: () => void; small?: boolean }> = ({ src, onRemove, small }) => (
  <Box sx={{ position: 'relative', display: 'inline-block', mt: 0.5 }}>
    <Box
      component="img"
      src={src}
      alt="Screenshot"
      sx={{
        maxWidth: small ? 120 : 220,
        maxHeight: small ? 80 : 160,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.300',
        cursor: 'pointer',
      }}
      onClick={() => window.open(src, '_blank')}
    />
    {onRemove && (
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          position: 'absolute', top: -8, right: -8,
          bgcolor: 'error.main', color: 'white',
          width: 20, height: 20,
          '&:hover': { bgcolor: 'error.dark' },
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Main ChatIA Component
// ---------------------------------------------------------------------------

const ChatIA: React.FC<ChatIAProps> = ({
  contexto = 'dashboard',
  dashboardId,
  submenuId,
  eixo,
  quadrimestre,
  dadosContexto,
  captureRef,
  open,
  onClose,
}) => {
  const { municipioAtivo } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Internal navigation handler: navigate and close chat drawer
  const handleInternalNavigate = useCallback((path: string) => {
    onClose();
    // Small delay to let the drawer close animation start
    setTimeout(() => navigate(path), 150);
  }, [navigate, onClose]);

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [conversationId, setConversationId] = useState<number | undefined>();
  const [pendingImage, setPendingImage] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [drawerHidden, setDrawerHidden] = useState(false);
  const [pendingSearch, setPendingSearch] = useState<SearchPending | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const nextIdRef = useRef(1);

  // Auto-scroll
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  // Fetch suggestions on open
  useEffect(() => {
    if (open && municipioAtivo?.id) {
      iaService.getSuggestions(municipioAtivo.id, contexto, eixo)
        .then(setSuggestions)
        .catch(() => setSuggestions(getDefaultSuggestions(contexto, eixo)));
    }
  }, [open, municipioAtivo?.id, contexto, eixo]);

  // ---------------------------------------------------------------------------
  // Image Handling
  // ---------------------------------------------------------------------------

  const processImageFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 4 * 1024 * 1024) {
      alert('Imagem muito grande (max 4MB). Tente reduzir a resolucao.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setPendingImage(dataUrl);
    };
    reader.readAsDataURL(file);
  }, []);

  // Paste handler (Ctrl+V)
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) processImageFile(file);
        return;
      }
    }
  }, [processImageFile]);

  // File upload
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processImageFile(file);
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [processImageFile]);

  // Screen capture via Screen Capture API (captures real pixels, including cross-origin iframes)
  // Temporarily hides the chat drawer so it doesn't appear in the screenshot
  const handleCaptureScreen = useCallback(async () => {
    setCapturing(true);

    // 1. Hide the drawer so it doesn't appear in the capture
    setDrawerHidden(true);
    // Wait for the drawer close animation to complete
    await new Promise((r) => setTimeout(r, 400));

    try {
      // 2. Request screen/tab capture (user picks the tab)
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' } as any,
        audio: false,
      });

      // Grab a single frame from the video stream
      const track = stream.getVideoTracks()[0];
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;

      await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => { video.play(); resolve(); };
      });
      // Wait a frame to ensure the video has actual content
      await new Promise((r) => requestAnimationFrame(r));

      const canvas = document.createElement('canvas');

      // If captureRef is available, crop to that area
      if (captureRef?.current) {
        const rect = captureRef.current.getBoundingClientRect();
        const scaleX = video.videoWidth / window.innerWidth;
        const scaleY = video.videoHeight / window.innerHeight;

        canvas.width = Math.round(rect.width * scaleX);
        canvas.height = Math.round(rect.height * scaleY);
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(
          video,
          Math.round(rect.left * scaleX), Math.round(rect.top * scaleY),
          canvas.width, canvas.height,
          0, 0,
          canvas.width, canvas.height,
        );
      } else {
        // Full screen capture
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')!.drawImage(video, 0, 0);
      }

      // Stop the stream immediately (we only needed one frame)
      track.stop();

      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      setPendingImage(dataUrl);
    } catch (err: any) {
      // User cancelled the screen share dialog
      if (err.name !== 'NotAllowedError') {
        console.error('Erro ao capturar tela:', err);
      }
    } finally {
      // 3. Re-open the drawer and clear capturing state
      setDrawerHidden(false);
      setCapturing(false);
    }
  }, [captureRef]);

  // ---------------------------------------------------------------------------
  // Send Message (with streaming)
  // ---------------------------------------------------------------------------

  const handleSend = async () => {
    if ((!inputValue.trim() && !pendingImage) || loading) return;
    const text = inputValue.trim() || (pendingImage ? 'Analise esta imagem do dashboard.' : '');
    const imageToSend = pendingImage;
    setInputValue('');
    setPendingImage(null);

    const userMsg: Message = {
      id: nextIdRef.current++,
      role: 'user',
      content: text,
      timestamp: new Date(),
      imagePreview: imageToSend || undefined,
    };

    const assistantId = nextIdRef.current++;
    const assistantMsg: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setLoading(true);

    // Build history from previous messages
    const history = messages
      .filter(m => m.content && !m.isStreaming)
      .slice(-10)
      .map(m => ({ role: m.role, content: m.content }));

    const request: ChatRequest = {
      municipio_id: municipioAtivo?.id || 0,
      contexto,
      dashboard_id: dashboardId,
      eixo,
      quadrimestre,
      pergunta: text,
      historico: history,
      conversation_id: conversationId,
      dados_contexto: dadosContexto,
      imagem_base64: imageToSend || undefined,
    };

    try {
      const { reader: readerPromise, abort } = iaService.chatStream(request);
      abortRef.current = abort;

      const reader = await readerPromise;
      if (!reader) throw new Error('Streaming nao suportado');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const block of lines) {
          const trimmed = block.trim();
          if (!trimmed || trimmed === 'data: [DONE]') continue;

          const match = trimmed.match(/^data:\s*(.+)$/s);
          if (!match) continue;

          try {
            const event = JSON.parse(match[1]);
            if (event.text) {
              fullText += event.text;

              // Detect [SEARCH_NEEDED]{"query":"...","motivo":"..."}
              const searchMatch = fullText.match(/\[SEARCH_NEEDED\](\{[^}]+\})\s*$/);
              if (searchMatch) {
                try {
                  const searchData = JSON.parse(searchMatch[1]);
                  const cleanContent = fullText.slice(0, searchMatch.index).trimEnd();
                  setMessages(prev =>
                    prev.map(m => m.id === assistantId ? { ...m, content: cleanContent, isStreaming: false } : m),
                  );
                  setPendingSearch({
                    query: searchData.query,
                    motivo: searchData.motivo,
                    messageId: assistantId,
                    partialContent: cleanContent,
                  });
                  // Abort current stream — we'll continue after user decision
                  abortRef.current?.abort();
                  setLoading(false);
                  abortRef.current = null;
                  return; // Exit handleSend entirely
                } catch { /* JSON parse failed, treat as normal text */ }
              }

              // Hide potential partial tag from display
              let displayText = fullText;
              const partialIdx = fullText.lastIndexOf('[SEARCH_N');
              if (partialIdx >= 0 && partialIdx > fullText.length - 80) {
                displayText = fullText.slice(0, partialIdx);
              }

              setMessages(prev =>
                prev.map(m => m.id === assistantId ? { ...m, content: displayText } : m),
              );
            } else if (event.error) {
              fullText = `Erro: ${event.error}`;
              setMessages(prev =>
                prev.map(m => m.id === assistantId ? { ...m, content: fullText } : m),
              );
            }
          } catch { /* ignore parse errors */ }
        }
      }

      // Finalize
      setMessages(prev =>
        prev.map(m => m.id === assistantId ? { ...m, isStreaming: false } : m),
      );
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setMessages(prev =>
          prev.map(m => m.id === assistantId
            ? { ...m, isStreaming: false, content: m.content || '(cancelado)' }
            : m),
        );
      } else {
        console.error('Chat IA error:', error);
        const errorMsg = error.message || 'Erro desconhecido';

        // If streaming failed, try non-streaming fallback
        if (!errorMsg.includes('403') && !errorMsg.includes('permissao')) {
          try {
            const result = await iaService.chat(request);
            setMessages(prev =>
              prev.map(m => m.id === assistantId
                ? { ...m, content: result.resposta, isStreaming: false }
                : m),
            );
            if (result.conversation_id) setConversationId(result.conversation_id);
            return;
          } catch { /* fallback also failed */ }
        }

        setMessages(prev =>
          prev.map(m => m.id === assistantId
            ? { ...m, content: `**Erro:** ${errorMsg}\n\nTente novamente ou verifique as configuracoes do provedor de IA.`, isStreaming: false }
            : m),
        );
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const handleCancel = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const handleClear = () => {
    handleCancel();
    setMessages([]);
    setConversationId(undefined);
    setPendingImage(null);
  };

  // ---------------------------------------------------------------------------
  // Web Search Approve / Deny
  // ---------------------------------------------------------------------------

  const handleSearchApprove = useCallback(async () => {
    if (!pendingSearch || !municipioAtivo?.id) return;

    setLoading(true);

    const newAssistantId = nextIdRef.current++;
    setMessages(prev => [...prev, {
      id: newAssistantId,
      role: 'assistant' as const,
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    }]);

    const search = pendingSearch;
    setPendingSearch(null);

    const history = messages
      .filter(m => m.content && !m.isStreaming)
      .slice(-10)
      .map(m => ({ role: m.role, content: m.content }));

    const lastUserMsg = messages.filter(m => m.role === 'user').pop();

    const request: WebSearchRequest = {
      municipio_id: municipioAtivo.id,
      contexto,
      query: search.query,
      pergunta: lastUserMsg?.content || '',
      historico: history,
      conversation_id: conversationId,
      dashboard_id: dashboardId,
      eixo,
      quadrimestre,
      dados_contexto: dadosContexto,
      resposta_parcial: search.partialContent || undefined,
    };

    try {
      const { reader: readerPromise, abort } = iaService.webSearchStream(request);
      abortRef.current = abort;
      const reader = await readerPromise;
      if (!reader) throw new Error('Streaming nao suportado');

      const decoder = new TextDecoder();
      let sseBuffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n\n');
        sseBuffer = lines.pop() || '';

        for (const block of lines) {
          const trimmed = block.trim();
          if (!trimmed || trimmed === 'data: [DONE]') continue;
          const match = trimmed.match(/^data:\s*(.+)$/s);
          if (!match) continue;
          try {
            const event = JSON.parse(match[1]);
            if (event.text) {
              fullText += event.text;
              setMessages(prev =>
                prev.map(m => m.id === newAssistantId ? { ...m, content: fullText } : m),
              );
            }
          } catch { /* ignore */ }
        }
      }

      setMessages(prev =>
        prev.map(m => m.id === newAssistantId ? { ...m, isStreaming: false } : m),
      );
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        setMessages(prev =>
          prev.map(m => m.id === newAssistantId
            ? { ...m, content: 'Erro ao realizar pesquisa. Tente novamente.', isStreaming: false }
            : m),
        );
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, [pendingSearch, municipioAtivo, messages, contexto, conversationId, dashboardId, eixo, quadrimestre, dadosContexto]);

  const handleSearchDeny = useCallback(() => {
    if (!pendingSearch) return;
    setMessages(prev =>
      prev.map(m => m.id === pendingSearch.messageId
        ? {
            ...m,
            content: (pendingSearch.partialContent ? pendingSearch.partialContent + '\n\n' : '') +
              '*Pesquisa web nao autorizada. Respondendo com os dados disponiveis.*',
            isStreaming: false,
          }
        : m),
    );
    setPendingSearch(null);
    setLoading(false);
  }, [pendingSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Drawer
      anchor="right"
      open={open && !drawerHidden}
      onClose={onClose}
      PaperProps={{
        sx: { width: isMobile ? '100%' : 560, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <AppBar position="static" elevation={0}>
        <Toolbar variant="dense">
          <BrainIcon sx={{ mr: 1.5 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Assistente IA
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {municipioAtivo?.nome || 'NeoGestor'}
              {contexto === 'saude' && ' - Saude APS'}
            </Typography>
          </Box>
          {messages.length > 0 && (
            <Tooltip title="Limpar conversa">
              <IconButton color="inherit" onClick={handleClear} size="small">
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton edge="end" color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Messages */}
      <Box sx={{
        flexGrow: 1, overflow: 'auto', py: 2, backgroundColor: 'grey.50',
        display: 'flex', flexDirection: 'column', gap: 1.5,
      }}>
        {/* Welcome + suggestions */}
        {messages.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <AIIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {contexto === 'saude'
                ? 'Posso analisar os indicadores de saude do seu municipio. Como posso ajudar?'
                : 'Como posso ajudar a analisar os dados do seu municipio?'}
            </Typography>
            {contexto === 'dashboard' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Dica: Cole um screenshot (Ctrl+V) ou use o botao de camera para enviar uma imagem do dashboard.
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2, px: 1 }}>
              {(suggestions.length > 0 ? suggestions : getDefaultSuggestions(contexto, eixo))
                .slice(0, 4)
                .map((q, i) => (
                  <Chip
                    key={i}
                    label={q}
                    variant="outlined"
                    size="small"
                    onClick={() => { setInputValue(q); inputRef.current?.focus(); }}
                    sx={{ cursor: 'pointer', justifyContent: 'flex-start', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                  />
                ))}
            </Box>
          </Box>
        )}

        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
              gap: 1, px: 1.5,
            }}
          >
            {message.role === 'assistant' && (
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <AIIcon fontSize="small" />
              </Avatar>
            )}
            <Box sx={{ maxWidth: '80%' }}>
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  bgcolor: message.role === 'user' ? 'primary.main' : 'white',
                  color: message.role === 'user' ? 'white' : 'text.primary',
                  borderRadius: 2,
                  borderTopLeftRadius: message.role === 'user' ? 16 : 4,
                  borderTopRightRadius: message.role === 'user' ? 4 : 16,
                }}
              >
                {/* User image attachment */}
                {message.imagePreview && (
                  <ImagePreview src={message.imagePreview} small />
                )}
                {message.role === 'user' ? (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>
                ) : message.isStreaming && !message.content ? (
                  <TypingIndicator />
                ) : (
                  <MarkdownContent content={message.content} onNavigate={handleInternalNavigate} />
                )}
              </Paper>
              {message.role === 'assistant' && message.content && !message.isStreaming && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  <CopyButton text={message.content} />
                  <Typography variant="caption" color="text.secondary">
                    {message.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              )}
            </Box>
            {message.role === 'user' && (
              <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                <PersonIcon fontSize="small" />
              </Avatar>
            )}
          </Box>
        ))}

        {loading && messages[messages.length - 1]?.isStreaming && messages[messages.length - 1]?.content && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
            <CircularProgress size={16} />
          </Box>
        )}

        {/* Web Search Approval Card */}
        {pendingSearch && (
          <Box sx={{ px: 1.5, py: 1 }}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'info.light',
                bgcolor: 'rgba(2, 136, 209, 0.04)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TravelExploreIcon color="info" />
                <Typography variant="subtitle2" fontWeight={600}>
                  Pesquisa na Internet
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {pendingSearch.motivo}
              </Typography>
              <Chip
                label={pendingSearch.query}
                size="small"
                variant="outlined"
                sx={{ mb: 1.5, fontFamily: 'monospace', fontSize: '0.75rem' }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  icon={loading ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                  label={loading ? 'Pesquisando...' : 'Autorizar'}
                  color="primary"
                  onClick={loading ? undefined : handleSearchApprove}
                  disabled={loading}
                  sx={{ cursor: loading ? 'default' : 'pointer', fontWeight: 600 }}
                />
                <Chip
                  label="Nao, usar dados locais"
                  variant="outlined"
                  onClick={loading ? undefined : handleSearchDeny}
                  disabled={loading}
                  sx={{ cursor: loading ? 'default' : 'pointer' }}
                />
              </Box>
            </Paper>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {/* Pending image preview */}
      {pendingImage && (
        <Box sx={{ px: 1.5, pt: 1, backgroundColor: 'white' }}>
          <ImagePreview src={pendingImage} onRemove={() => setPendingImage(null)} />
        </Box>
      )}

      {/* Input */}
      <Box sx={{ p: 1.5, backgroundColor: 'white' }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
          {/* Image actions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Tooltip title="Enviar imagem">
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                sx={{ color: 'grey.500' }}
              >
                <ImageIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Capturar tela do dashboard">
              <IconButton
                size="small"
                onClick={handleCaptureScreen}
                disabled={loading || capturing}
                sx={{ color: 'grey.500' }}
              >
                {capturing ? <CircularProgress size={18} /> : <CameraIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>

          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={pendingImage ? "Descreva o que quer saber sobre a imagem..." : "Digite sua pergunta (ou cole Ctrl+V)..."}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={loading}
            variant="outlined"
            size="small"
            inputRef={inputRef}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          {loading ? (
            <IconButton
              color="error"
              onClick={handleCancel}
              sx={{ bgcolor: 'error.light', color: 'white', '&:hover': { bgcolor: 'error.main' } }}
            >
              <StopIcon />
            </IconButton>
          ) : (
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!inputValue.trim() && !pendingImage}
              sx={{
                bgcolor: 'primary.main', color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' },
              }}
            >
              <SendIcon />
            </IconButton>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Enter envia, Shift+Enter nova linha, Ctrl+V cola screenshot
        </Typography>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          hidden
          onChange={handleFileSelect}
        />
      </Box>
    </Drawer>
  );
};

export default ChatIA;

// ---------------------------------------------------------------------------
// ChatIAButton (FAB)
// ---------------------------------------------------------------------------

export const ChatIAButton: React.FC<{
  contexto?: 'dashboard' | 'saude';
  dashboardId?: number;
  submenuId?: number;
  eixo?: string;
  quadrimestre?: string;
  dadosContexto?: string;
  captureRef?: React.RefObject<HTMLElement | null>;
  fabSx?: object;
}> = ({ contexto = 'dashboard', dashboardId, submenuId, eixo, quadrimestre, dadosContexto, captureRef, fabSx }) => {
  const [open, setOpen] = useState(false);
  const isOffline = process.env.REACT_APP_OFFLINE_MODE === 'true';

  if (isOffline) {
    return (
      <Tooltip title="Chat IA indisponivel no modo offline">
        <span>
          <Fab
            color="default"
            size="small"
            aria-label="IA Offline"
            disabled
            sx={{
              position: 'fixed',
              top: 168,
              right: 24,
              zIndex: 1000,
              boxShadow: 3,
              ...fabSx,
            }}
          >
            <BrainIcon />
          </Fab>
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <Fab
        color="primary"
        size="small"
        aria-label="Assistente IA"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          top: 168,
          right: 24,
          zIndex: 1000,
          boxShadow: 3,
          ...fabSx,
        }}
      >
        <BrainIcon />
      </Fab>
      <ChatIA
        contexto={contexto}
        dashboardId={dashboardId}
        submenuId={submenuId}
        eixo={eixo}
        quadrimestre={quadrimestre}
        dadosContexto={dadosContexto}
        captureRef={captureRef}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Default suggestions
// ---------------------------------------------------------------------------

function getDefaultSuggestions(contexto: string, eixo?: string): string[] {
  if (contexto === 'saude' && eixo) {
    return [
      'Quais indicadores estao mais criticos e por que?',
      'Qual acao pratica teria maior impacto no score?',
      'Quantas pessoas faltam atender para subir a classificacao?',
      'Quais equipes estao com pior desempenho neste eixo?',
    ];
  }
  if (contexto === 'saude') {
    return [
      'Qual o diagnostico geral do meu municipio na APS?',
      'Quanto dinheiro estamos perdendo por nao estar Otimo?',
      'Se eu pudesse focar em apenas uma coisa, qual seria?',
      'Quais equipes precisam de intervencao urgente?',
    ];
  }
  return [
    'Quais sao os principais indicadores?',
    'Ha alguma tendencia preocupante?',
    'Que acoes voce recomenda?',
    'Resuma os pontos mais importantes.',
  ];
}
